import type {
  DataGridProProps,
  GridColDef,
  GridColumnHeaderParams,
  GridGroupNode,
  GridRenderCellParams,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-pro';
import { GridBooleanCell } from '@mui/x-data-grid-pro';
import type {
  TableColumnDTO,
  TableColumnIdentifier,
  TableColumnSystemField,
} from '@tempo-io/roadmap-dto';
import { DEPENDENCIES_COLUMN_WIDTH } from '@tempo-io/roadmap-dto';
import {
  AddLink,
  ArrowDropDownCircle,
  CalendarMonth,
  CheckCircle,
  Dependency,
  ExternalLink,
  Group,
  Groups,
  Link,
  Money,
  MonitorHeart,
  NoteAlt,
  Notes,
  Numbers,
  Person,
  PlaylistAddCheckCircle,
  Subject,
  TextFields,
  Timeline,
  Timer,
} from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomField } from '@/lib/api/types/customField';
import { isDialogEditedCustomFieldType } from '@/lib/api/types/customField';
import type { EstimationScheme } from '@/lib/api/types/estimationScheme';
import type { ItemStatus } from '@/lib/api/types/itemStatus';
import type { ItemTypeLevel, ItemTypeMap } from '@/lib/api/types/itemType';
import type { PickListOption } from '@/lib/api/types/picklistOption';
import type { Team } from '@/lib/api/types/teams';
import { usePermission } from '@/lib/hooks/common/usePermission';
import { useGetCustomFieldsQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import { useGetEstimationSchemesQuery } from '@/lib/queries/common/useGetEstimationScheme';
import { useGetItemStatusesQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useSortableItemFields } from '@/lib/queries/common/useItemFieldsQuery';
import { useItemTypeLevelsByIdQuery } from '@/lib/queries/common/useItemTypeLevelsQuery';
import { useItemTypesByIdQuery } from '@/lib/queries/common/useItemTypesQuery';
import { useGetTeamsByIdQuery } from '@/lib/queries/teams/useGetTeamsQuery';
import { isCustomFieldApplicableToItemType } from '@/lib/utils/loop-visualization/isCustomFieldApplicableToItemType';
import { CurrencyCell } from '../cells/CurrencyCell';
import { CustomFieldEditCell } from '../cells/CustomFieldEditCell';
import { DateCell } from '../cells/DateCell';
import { DependencyStatusCell } from '../cells/DependencyStatusCell';
import { DurationCell } from '../cells/DurationCell';
import { EstimationDisplayCell } from '../cells/EstimationDisplayCell';
import { EstimationEditCell } from '../cells/EstimationEditCell';
import { HealthTableCell } from '../cells/HealthTableCell';
import { IntegrationDataGridCell } from '../cells/IntegrationDataGridCell';
import { ItemNameCell } from '../cells/ItemNameCell';
import { ItemRefCell } from '../cells/ItemRefCell';
import { ItemStatusCell } from '../cells/ItemStatusCell';
import { ItemTypeEditCell } from '../cells/ItemTypeEditCell';
import { LinkCell } from '../cells/LinkCell';
import { MultiCustomerCell } from '../cells/MultiCustomerCell';
import { MultiItemRefCell } from '../cells/MultiItemRefCell';
import { MultiLineTextCell } from '../cells/MultiLineTextCell';
import { MultiLinkCell } from '../cells/MultiLinkCell';
import { MultiPickListCell } from '../cells/MultiPickListCell';
import { MultiTextCell } from '../cells/MultiTextCell';
import { MultiUserCell } from '../cells/MultiUserCell';
import { NumericCell } from '../cells/NumericCell';
import { PickListCell } from '../cells/PickListCell';
import { PlannedDateEditCell } from '../cells/PlannedDateEditCell';
import { ProgressTableCell } from '../cells/ProgressTableCell';
import { ScheduleDateCell } from '../cells/ScheduleDateCell';
import { StatusEditCell } from '../cells/StatusEditCell';
import { TargetDateEditCell } from '../cells/TargetDateEditCell';
import { TeamEditCell } from '../cells/TeamEditCell';
import { TextCell } from '../cells/TextCell';
import { TimeSpentVsPlannedCell } from '../cells/TimeSpentVsPlannedCell';
import { UserCell } from '../cells/UserCell';
import { EditHintWrapper, withEditHint, withSkeleton } from '../cells/withSkeleton';
import type { ItemsTableRow } from '../types';
import { getCellValue } from './getCellValue';
import { getColumnName } from './getColumnName';
import { renderColumnHeader } from './renderColumnHeader';
import { isItemFieldSortable } from './sortableItemFields';
import { buildTableColumnField } from './tableColumnUtils';

type CustomFieldColDef = Omit<GridColDef<ItemsTableRow>, 'renderHeader'> & {
  renderHeader?: (params: GridColumnHeaderParams) => React.ReactNode;
  isCellEditableForRow?: (row: ItemsTableRow) => boolean;
};

const emptyCustomFields: CustomField[] = [];
const emptyEstimationSchemes: EstimationScheme[] = [];
const emptyEstimationDataById: Record<number, { schemeName: string; label: string }> = {};
const emptyItemTypesById: ItemTypeMap = {};
const emptyItemTypeLevelsById: Record<number, ItemTypeLevel> = {};
const emptyItemStatuses: ItemStatus[] = [];
const emptyTeamsById: Record<Team['id'], Team> = {};

export const MIN_COLUMN_WIDTH = 100;
export const MIN_NAME_COLUMN_WIDTH = 240;

export const useTableColumns = (
  layoutColumns: TableColumnDTO[],
  hasRows: boolean,
  options?: { isLoading?: boolean; targetDatesEditable?: boolean; loopId?: string },
) => {
  const { t } = useTranslation();
  const isReady = Boolean(layoutColumns?.length);
  const isLoading = options?.isLoading ?? false;
  const targetDatesEditable = options?.targetDatesEditable ?? false;
  const loopId = options?.loopId;

  const { data: itemTypesById = emptyItemTypesById } = useItemTypesByIdQuery({ enabled: isReady });
  const { data: itemTypeLevelsById = emptyItemTypeLevelsById } = useItemTypeLevelsByIdQuery({
    enabled: isReady,
  });
  const { sortableItemFields } = useSortableItemFields();
  const canEditIntegrations = usePermission('edit_integrations');

  const groupingColWidth = layoutColumns?.find((col) => col.type === 'name')?.width;
  const groupingColDef = React.useMemo<DataGridProProps['groupingColDef']>(
    () =>
      ({
        headerName: t(($) => $.views.table.columns.systemFields.name),
        renderHeader: renderColumnHeader(Notes),
        valueGetter: (_value, row: ItemsTableRow) => row.name,
        renderCell: withSkeleton(
          (cellParams: GridRenderCellParams) => (
            <ItemNameCell
              {...(cellParams as GridRenderCellParams<
                ItemsTableRow,
                string,
                string,
                GridGroupNode
              >)}
              itemType={
                cellParams.row.itemTypeId ? itemTypesById[cellParams.row.itemTypeId] : undefined
              }
            />
          ),
          !hasRows,
        ),
        minWidth: MIN_NAME_COLUMN_WIDTH,
        flex: !groupingColWidth ? 1 : undefined,
        hideDescendantCount: true,
        hideable: false,
        sortable: true,
        disableColumnMenu: false,
        width: !groupingColWidth ? undefined : groupingColWidth,
        pinnable: false,
      }) as DataGridProProps['groupingColDef'],
    [t, itemTypesById, hasRows, groupingColWidth],
  );

  // We'll get data for columns content and layout differently when new API is ready
  const columnTypes = React.useMemo(
    () => new Set(layoutColumns?.map((col) => col.type)),
    [layoutColumns],
  );
  const { data: itemStatuses = emptyItemStatuses } = useGetItemStatusesQuery({
    enabled: isReady && columnTypes.has('status'),
  });
  const { data: customFields = emptyCustomFields } = useGetCustomFieldsQuery({
    enabled: isReady && columnTypes.has('customField'),
  });
  const { data: teamsById = emptyTeamsById } = useGetTeamsByIdQuery({
    enabled: isReady && columnTypes.has('team'),
  });
  const { data: estimationSchemes = emptyEstimationSchemes } = useGetEstimationSchemesQuery({
    enabled: isReady && (columnTypes.has('estimationScheme') || columnTypes.has('estimation')),
  });
  const estimationDataById = React.useMemo(() => {
    if (estimationSchemes.length === 0) return emptyEstimationDataById;
    const map: Record<number, { schemeName: string; label: string }> = {};
    for (const scheme of estimationSchemes) {
      for (const estimation of scheme.estimations) {
        map[estimation.id] = { schemeName: scheme.name, label: estimation.label };
      }
    }
    return map;
  }, [estimationSchemes]);

  const columns = React.useMemo(() => {
    // Set a data source for column(s) content and layout later
    const customFieldById = new Map(customFields.map((field) => [field.id, field]));
    const itemStatusById = new Map(itemStatuses.map((status) => [status.id, status]));
    const customFieldPickListOptionById: Map<
      CustomField['id'],
      Map<PickListOption['id'], PickListOption>
    > = new Map();
    // Populate picklist options map on demand
    const getCustomFieldPickListOptionById = (
      customField: CustomField,
      pickListOptionId?: PickListOption['id'],
    ) => {
      if (
        !customField ||
        !['PICKLIST', 'MULTI_PICKLIST'].includes(customField.type) ||
        !pickListOptionId
      )
        return undefined;

      const customFieldId = customField.id;
      if (!customFieldPickListOptionById.has(customFieldId)) {
        customFieldPickListOptionById.set(
          customFieldId,
          new Map((customField.picklistOptions || []).map((option) => [option.id, option])),
        );
      }

      return customFieldPickListOptionById.get(customFieldId)?.get(pickListOptionId);
    };

    const getColumnCellValue = (item: ItemsTableRow, columnIdentifier: TableColumnIdentifier) =>
      getCellValue(item, columnIdentifier, {
        customFieldById,
        itemStatusById,
        itemTypesById,
        teamsById,
        getCustomFieldPickListOptionById,
        estimationDataById,
      });

    return (layoutColumns ?? [])
      .map(({ width, ...columnIdentifier }) => {
        const { type, customFieldId } = columnIdentifier;
        const customField =
          type === 'customField' && customFieldId ? customFieldById.get(customFieldId) : undefined;
        const commonProps: Pick<
          GridColDef<ItemsTableRow>,
          | 'field'
          | 'type'
          | 'headerName'
          | 'valueGetter'
          | 'minWidth'
          | 'width'
          | 'hideable'
          | 'renderEditCell'
          | 'editable'
          | 'pinnable'
          | 'sortable'
        > = {
          field: buildTableColumnField(columnIdentifier),
          type: 'string',
          headerName: getColumnName(t, columnIdentifier, customField),
          valueGetter: (_value: unknown, row: ItemsTableRow) =>
            getColumnCellValue(row, columnIdentifier),
          minWidth: MIN_COLUMN_WIDTH,
          width,
          hideable: false,
          pinnable: true,
          sortable:
            type === 'customField' ||
            isItemFieldSortable(type as TableColumnSystemField, sortableItemFields),
        };

        switch (type) {
          case 'name':
            // Handled in groupingColDef
            return null;

          case 'id':
            return {
              ...commonProps,
              type: 'number',
              align: 'left',
              headerAlign: 'left',
              renderHeader: renderColumnHeader(Numbers),
              renderCell: withSkeleton(TextCell, !hasRows),
            };

          case 'externalKey':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(ExternalLink),
              renderCell: withSkeleton(LinkCell, !hasRows),
            };

          case 'progress':
            return {
              ...commonProps,
              type: 'number',
              renderHeader: renderColumnHeader(Numbers),
              headerAlign: 'left',
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <ProgressTableCell row={params.row} />
              ),
            };

          // System date fields
          case 'createdAt':
          case 'updatedAt':
          case 'doneDate':
            return {
              ...commonProps,
              type: 'dateTime',
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: withSkeleton(DateCell, !hasRows),
            };

          case 'earliestStartDate':
          case 'deadlineDate': {
            const dateField =
              type === 'earliestStartDate' ? 'earliestStartDateTime' : 'deadlineDateTime';
            if (!targetDatesEditable) {
              return {
                ...commonProps,
                type: 'dateTime',
                renderHeader: renderColumnHeader(CalendarMonth),
                renderCell: withSkeleton(DateCell, !hasRows),
              };
            }
            return {
              ...commonProps,
              type: 'dateTime',
              editable: true,
              editableForExternalItems: true,
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: withEditHint(withSkeleton(DateCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <TargetDateEditCell dateField={dateField} {...params} />
              ),
            };
          }

          case 'plannedStartDate':
            return {
              ...commonProps,
              type: 'dateTime',
              editable: true,
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: withEditHint(withSkeleton(DateCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <PlannedDateEditCell dateField="plannedStartDateTime" {...params} />
              ),
            };

          case 'plannedFinishDate':
            return {
              ...commonProps,
              type: 'dateTime',
              editable: true,
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: withEditHint(withSkeleton(DateCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <PlannedDateEditCell dateField="plannedFinishDateTime" {...params} />
              ),
            };

          case 'expectedStartDate':
            return {
              ...commonProps,
              type: 'dateTime',
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <ScheduleDateCell row={params.row} field="forecastedStartDateTime" />
              ),
            };

          case 'expectedFinishDate':
            return {
              ...commonProps,
              type: 'dateTime',
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <ScheduleDateCell row={params.row} field="forecastedFinishDateTime" />
              ),
            };

          case 'latestFinishDate':
            return {
              ...commonProps,
              type: 'dateTime',
              renderHeader: renderColumnHeader(CalendarMonth),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <ScheduleDateCell row={params.row} field="latestFinishDateTime" />
              ),
            };

          case 'dependencies':
            return {
              ...commonProps,
              sortable: false,
              filterable: false,
              minWidth: DEPENDENCIES_COLUMN_WIDTH,
              width: width ?? DEPENDENCIES_COLUMN_WIDTH,
              valueGetter: (_: unknown, row: ItemsTableRow) => row.dependencies?.length ?? 0,
              renderHeader: () => (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={commonProps.headerName}
                        className="flex w-full cursor-default items-center justify-center"
                      >
                        <Dependency />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{commonProps.headerName}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <DependencyStatusCell row={params.row} />
              ),
            };

          case 'status':
            return {
              ...commonProps,
              editable: true,
              renderHeader: renderColumnHeader(ArrowDropDownCircle),
              renderCell: withEditHint(withSkeleton(ItemStatusCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <StatusEditCell {...params} />
              ),
            };

          case 'createdBy':
          case 'updatedBy':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(Person),
              renderCell: (params: GridRenderCellParams<ItemsTableRow, string>) => (
                <UserCell {...params} />
              ),
            };

          case 'itemType':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(Notes),
              editable: true,
              renderCell: withEditHint(withSkeleton(TextCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <ItemTypeEditCell
                  itemTypesById={itemTypesById}
                  itemTypeLevelsById={itemTypeLevelsById}
                  {...params}
                />
              ),
              isCellEditableForRow: (row: ItemsTableRow) => {
                if (!row.itemTypeId) return false;
                const current = itemTypesById[row.itemTypeId];
                if (!current) return false;
                const currentLevel = itemTypeLevelsById[current.itemLevelId];
                if (!currentLevel) return false;
                return (
                  Object.values(itemTypesById).filter((t) => {
                    const level = itemTypeLevelsById[t.itemLevelId];
                    return level && level.level === currentLevel.level;
                  }).length > 1
                );
              },
            };

          case 'team':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(Groups),
              renderCell: withEditHint(withSkeleton(TextCell, !hasRows)),
              editable: !isLoading,
              renderEditCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <TeamEditCell
                  teamsById={teamsById}
                  loopId={loopId ? Number(loopId) : undefined}
                  {...params}
                />
              ),
            };

          case 'estimationScheme':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(Timer),
              renderCell: withSkeleton(TextCell, !hasRows),
            };

          case 'estimation':
            return {
              ...commonProps,
              editable: !isLoading,
              renderHeader: renderColumnHeader(Timer),
              renderCell: withEditHint(withSkeleton(EstimationDisplayCell, !hasRows)),
              renderEditCell: (params: GridRenderEditCellParams<ItemsTableRow>) => (
                <EstimationEditCell {...params} />
              ),
            };

          case 'timeSpent':
            return {
              ...commonProps,
              type: 'number',
              renderHeader: renderColumnHeader(Timer),
              align: 'left',
              headerAlign: 'left',
              renderCell: withSkeleton(DurationCell, !hasRows),
            };

          case 'health':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(MonitorHeart),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <HealthTableCell row={params.row} />
              ),
            };

          case 'timeSpentVsPlanned':
            return {
              ...commonProps,
              renderHeader: renderColumnHeader(Timeline),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <TimeSpentVsPlannedCell row={params.row} />
              ),
            };

          case 'integration':
            return {
              ...commonProps,
              sortable: false,
              renderHeader: renderColumnHeader(Link),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
                <IntegrationDataGridCell
                  row={params.row}
                  itemTypesById={itemTypesById}
                  canEditIntegrations={canEditIntegrations}
                />
              ),
            };

          case 'customField': {
            if (!customField) return null;

            commonProps.editable = true;
            commonProps.renderEditCell = (params: GridRenderCellParams<ItemsTableRow>) => (
              <CustomFieldEditCell
                params={params}
                customField={customField}
                itemTypesById={itemTypesById}
              />
            );

            const buildColDef = (): CustomFieldColDef => {
              switch (customField.type) {
                case 'CURRENCY':
                  return {
                    ...commonProps,
                    type: 'number',
                    renderHeader: renderColumnHeader(Money),
                    headerAlign: 'left',
                    renderCell: withSkeleton(
                      ({ value }: GridRenderCellParams<ItemsTableRow>) => (
                        <CurrencyCell value={value} currencyUnit={customField.currencyUnit} />
                      ),
                      !hasRows,
                    ),
                  };

                case 'NUMERIC':
                  return {
                    ...commonProps,
                    type: 'number',
                    renderHeader: renderColumnHeader(Numbers),
                    headerAlign: 'left',
                    renderCell: withSkeleton(NumericCell, !hasRows),
                  };

                case 'DATE':
                  return {
                    ...commonProps,
                    type: 'dateTime',
                    renderHeader: renderColumnHeader(CalendarMonth),
                    renderCell: withSkeleton(DateCell, !hasRows),
                  };

                case 'USER':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Person),
                    renderCell: (params: GridRenderCellParams<ItemsTableRow, string>) => (
                      <UserCell {...params} />
                    ),
                  };

                case 'MULTI_USER':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Group),
                    renderCell: (params: GridRenderCellParams<ItemsTableRow, string[]>) => (
                      <MultiUserCell {...params} />
                    ),
                  };

                case 'MULTI_CUSTOMER':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Group),
                    renderCell: (params: GridRenderCellParams<ItemsTableRow, number[]>) => (
                      <MultiCustomerCell {...params} />
                    ),
                  };

                case 'ITEM':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(ExternalLink),
                    renderCell: (params: GridRenderCellParams<ItemsTableRow, number>) => (
                      <ItemRefCell {...params} itemTypesById={itemTypesById} />
                    ),
                  };

                case 'MULTI_ITEM':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(ExternalLink),
                    renderCell: (params: GridRenderCellParams<ItemsTableRow, number[]>) => (
                      <MultiItemRefCell {...params} itemTypesById={itemTypesById} />
                    ),
                  };

                case 'MULTILINE_TEXT':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Subject),
                    renderCell: withSkeleton(MultiLineTextCell, !hasRows),
                  };

                case 'NOTE':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(NoteAlt),
                    renderCell: withSkeleton(MultiLineTextCell, !hasRows),
                  };

                case 'TEXT':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(TextFields),
                    renderCell: withSkeleton(TextCell, !hasRows),
                  };

                case 'CHECKBOX':
                  return {
                    ...commonProps,
                    type: 'boolean',
                    renderHeader: renderColumnHeader(CheckCircle),
                    headerAlign: 'left',
                    renderCell: withSkeleton(GridBooleanCell, !hasRows),
                  };

                case 'MULTI_TEXT':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Notes),
                    renderCell: withSkeleton(MultiTextCell, !hasRows),
                  };

                case 'LINK':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(Link),
                    renderCell: withSkeleton(LinkCell, !hasRows),
                  };

                case 'MULTI_LINK':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(AddLink),
                    renderCell: withSkeleton(MultiLinkCell, !hasRows),
                  };

                case 'PICKLIST':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(ArrowDropDownCircle),
                    renderCell: withSkeleton(PickListCell, !hasRows),
                  };

                case 'MULTI_PICKLIST':
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(PlaylistAddCheckCircle),
                    renderCell: withSkeleton(MultiPickListCell, !hasRows),
                  };

                default:
                  return {
                    ...commonProps,
                    renderHeader: renderColumnHeader(),
                    renderCell: withSkeleton(TextCell, !hasRows),
                  };
              }
            };

            const colDef = buildColDef();
            const originalRenderCell = colDef.renderCell;
            const isEditable = true;
            const isDialogEdited = isDialogEditedCustomFieldType(customField.type);

            return {
              ...colDef,
              cellClassName: isDialogEdited ? 'dialog-edit-cell' : undefined,
              isCellEditableForRow: isEditable
                ? (row: ItemsTableRow) =>
                    row.itemTypeId === undefined ||
                    isCustomFieldApplicableToItemType(customField, row.itemTypeId)
                : undefined,
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) => {
                if (
                  params.row.itemTypeId !== undefined &&
                  !isCustomFieldApplicableToItemType(customField, params.row.itemTypeId)
                ) {
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex w-full h-full items-center">
                          {originalRenderCell?.(params)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(($) => $.views.table.customField.notApplicableToItemType)}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                const cell = originalRenderCell?.(params);
                if (isEditable && params.isEditable) {
                  return <EditHintWrapper>{cell}</EditHintWrapper>;
                }
                return cell;
              },
            };
          }

          // Unknown column type
          default:
            return null;
        }
      })
      .filter((col) => col !== null) as unknown as GridColDef<ItemsTableRow>[];
  }, [
    t,
    hasRows,
    customFields,
    layoutColumns,
    itemStatuses,
    teamsById,
    loopId,
    itemTypesById,
    itemTypeLevelsById,
    estimationDataById,
    sortableItemFields,
    isLoading,
    targetDatesEditable,
    canEditIntegrations,
  ]);

  return { groupingColDef, columns };
};
